import { Site } from "@datadog/browser-core/cjs/domain/configuration/intakeSites";
import { TEnvironmentalVariables } from "./models/env.models";
import { MILLISECONDS_IN_MINUTE } from "./utils/constants/time.constants";

const VARIABLE_INDICATION_PREFIX = "${";

const requiredVars = new Set<Partial<keyof TEnvironmentalVariables>>([
  "apiUrl",
  "platformTenant",
  "platformTenantType",
  "CKEditorKey",
]);

const getEnvVar = (
  key: keyof TEnvironmentalVariables,
  fallback?: string,
): string => {
  if (!window.ENV) {
    throw new Error("Environmental variables config was not provided.");
  }

  if (
    !window.ENV[key] ||
    window.ENV[key].startsWith(VARIABLE_INDICATION_PREFIX)
  ) {
    const variableName = key.replace(VARIABLE_INDICATION_PREFIX, "");

    if (fallback) {
      if (requiredVars.has(key)) {
        console.warn(
          `${variableName} was not provided! "${fallback}" value is used.`,
        );
      }

      return fallback;
    } else {
      if (requiredVars.has(key)) {
        console.warn(
          `${variableName} was not provided as well as a fallback value!`,
        );
      }

      return "";
    }
  } else {
    return window.ENV[key];
  }
};

export default function getEnv(): TEnvironmentalVariables {
  const apiUrl = getEnvVar("apiUrl");
  const graphQLServicePath = getEnvVar("graphQLServicePath", "/graphql/");

  return {
    accumulusPublicSite: getEnvVar(
      "accumulusPublicSite",
      "https://accumulus.org",
    ),
    accumulusSupportEmail: getEnvVar(
      "accumulusSupportEmail",
      "support@accumulus.org",
    ),
    appTitle: getEnvVar("appTitle", "Accumulus Synergy"),
    multitenancyEnabled: getEnvVar("multitenancyEnabled", "false"),
    mockApiKey: getEnvVar("mockApiKey"),
    mockApiUrl: getEnvVar("mockApiUrl"),
    graphQLUrl: getEnvVar("graphQLUrl", `${apiUrl}${graphQLServicePath}`),
    graphQLServicePath,
    apiUrl,
    assetServicePath: getEnvVar("assetServicePath", "/asset"),
    trackingServicePath: getEnvVar("trackingServicePath", "/audit"),
    identityServicePath: getEnvVar("identityServicePath", "/identity"),
    notificationsServicePath: getEnvVar(
      "notificationsServicePath",
      "/notification",
    ),
    workflowServicePath: getEnvVar("workflowServicePath", "/workflow"),
    CKEditorKey: getEnvVar("CKEditorKey"),
    platformTenant: getEnvVar("platformTenant"),
    platformTenantType: getEnvVar("platformTenantType"),
    idleTimeout: getEnvVar(
      "idleTimeout",
      (MILLISECONDS_IN_MINUTE * 15).toString(),
    ),
    sharedConfigUrl: getEnvVar(
      "sharedConfigUrl",
      "https://accusharedconfig.blob.core.windows.net/config/config.json",
    ),
    featureFlagsOverrides: getEnvVar("featureFlagsOverrides", ""),
    globalBannerConfigUrl: getEnvVar(
      "globalBannerConfigUrl",
      "https://accusharedconfig.blob.core.windows.net/config/global-banner-dev.json",
    ),
    datadogRUMEnabled: getEnvVar("datadogRUMEnabled", "false"),
    datadogSite: getEnvVar("datadogSite") as Site,
    datadogService: getEnvVar("datadogService"),
    datadogApplicationId: getEnvVar("datadogApplicationId"),
    datadogClientToken: getEnvVar("datadogClientToken"),
    datadogEnv: getEnvVar("datadogEnv"),
    datadogAppVersion: getEnvVar("datadogAppVersion"),
    datadogSessionSampleRate: getEnvVar("datadogSessionSampleRate"),
    datadogReplaySampleRate: getEnvVar("datadogReplaySampleRate"),
    datadogPrivacyLevel: getEnvVar("datadogPrivacyLevel"),
    datadogUserEmailVisible: getEnvVar("datadogUserEmailVisible"),
  };
}
